// TypeScript interfaces for Appraiser Site Builder

import {
  DEFAULT_FAQ_ITEMS,
  DEFAULT_PROCESS_STEPS,
  DEFAULT_TESTIMONIAL_PLACEHOLDERS,
} from '@/constants/appraisalServiceTypes';

export type SectionDividerStyle = 'wave' | 'angle' | 'curve' | 'zigzag' | 'clouds' | 'steps' | 'none';
export type NavbarStyle = 'glassy' | 'matte';

export interface SiteTheme {
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  // logoUrl is deprecated - now resolved from companies table
  logoUrl?: string | null;
  // New customization options
  sectionDivider?: SectionDividerStyle;
  navbarStyle?: NavbarStyle;
  navbarSticky?: boolean;
  heroParallax?: boolean;
}

export type HeroOverlayType = 'gradient' | 'solid' | 'none' | 'gradient-panel';
export type HeroTextAlign = 'left' | 'center' | 'right';
/** Blend direction for the 'gradient-panel' overlay: a side panel or a bottom scrim. */
export type HeroPanelBlend = 'side' | 'bottom';
export type FallbackBackground = 'gradient' | 'solid-primary' | 'solid-secondary' | 'neutral';

/** Curated icon set for hero trust badges. */
export type TrustBadgeIcon =
  | 'shield'
  | 'check'
  | 'star'
  | 'award'
  | 'clock'
  | 'users'
  | 'briefcase'
  | 'map-pin'
  | 'phone'
  | 'home';

/** A small trust-signal badge shown under the hero CTAs (e.g. "USPAP Compliant"). */
export interface TrustBadge {
  icon: TrustBadgeIcon;
  label: string;
}

export type HeroHeight = 'compact' | 'standard' | 'tall' | 'full';

export interface HeroContent {
  title: string;
  subtitle: string;
  backgroundImage: string | null;
  ctaText: string;
  ctaLink?: string;
  /** Optional secondary call-to-action shown beside the primary CTA. */
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  /** Small trust-signal badges shown under the hero CTAs. */
  trustBadges?: TrustBadge[];
  /** Vertical span of the hero. compact ≈ 40vh, standard ≈ 55vh, tall ≈ 70vh, full = 100vh. */
  heroHeight?: HeroHeight;
  /** Short eyebrow text rendered as an outline pill above the H1 (e.g. "Serving Bergen County, NJ"). */
  eyebrow?: string;
  /**
   * Comma-separated substrings to highlight inside the title with a
   * gradient-clip shimmer + soft glow. Matches are case-insensitive. Leave
   * blank to render the title normally.
   */
  titleHighlight?: string;
  /** When true, the background image is hidden below the `sm:` breakpoint so mobile gets a clean colored hero. */
  hideBackgroundOnMobile?: boolean;
  /** Show the appraiser's phone number as an in-hero CTA ("Questions? Call us: …"). */
  showHeroPhone?: boolean;
  overlayType?: HeroOverlayType;
  overlayColor?: string;
  overlayOpacity?: number;
  /** Blend direction for the 'gradient-panel' overlay type. Defaults to 'side'. */
  panelBlend?: HeroPanelBlend;
  /**
   * For the 'gradient-panel' overlay — percentage of the panel's axis that
   * stays fully opaque before the fade begins (10–70). Defaults to 32 for
   * side panels, 16 for bottom scrims.
   */
  panelCoverage?: number;
  /**
   * Where the gradient becomes fully transparent (panelCoverage + 5 to 100).
   * Defaults to `panelCoverage + 48`, capped at 95.
   */
  panelFadeEnd?: number;
  /**
   * Step the gradient through the site's secondary colour on the way to
   * transparent (primary → secondary → transparent). Defaults to false —
   * current sites keep the simpler primary → transparent fade.
   */
  panelUseSecondary?: boolean;
  /**
   * Where the secondary colour peaks in the fade (between panelCoverage and
   * panelFadeEnd). Defaults to the midpoint of the fade ramp. Only honored
   * when `panelUseSecondary` is on.
   */
  panelSecondaryStop?: number;
  /**
   * Add extra opacity stops between the secondary stop and the transparent
   * stop for a smoother, less banded fade. Recommended when primary and
   * secondary brand colors contrast a lot. Only honored when
   * `panelUseSecondary` is on.
   */
  panelSmoothFade?: boolean;
  textAlign?: HeroTextAlign;
  textColor?: string;
  /** Background style when no image is set */
  fallbackBackground?: FallbackBackground;
}

export interface AboutContent {
  title: string;
  bio: string;
  headshotUrl: string | null;
  yearsExperience: number | null;
  certifications: string[];
  whyChooseMe?: string[];
  hidePhoto?: boolean;
  hideBio?: boolean;
  hideLicenses?: boolean;
  hideDesignations?: boolean;
  hideCertifications?: boolean;
  hideQualifications?: boolean;
  hideWhyChooseMe?: boolean;
}

// Service item for structured service selection
export interface ServiceItem {
  id: string;
  label: string;
  description: string;
  category: string;
  enabled: boolean;
  customDescription?: string;
  /** Surfaces this service in the top "Featured Services" grid on the standalone /services page. Cap is enforced at render time. */
  featured?: boolean;
}

export interface ServicesContent {
  title: string;
  subtitle?: string;
  items: string[]; // Legacy - for backward compatibility
  selectedServices?: ServiceItem[]; // New structured services
  showDescriptions?: boolean;
  layoutStyle?: 'grid' | 'list' | 'categories';
}

export interface MapContent {
  title: string;
  /** 2-letter USPS state codes the appraiser serves. */
  states?: string[];
  /** County selections keyed by state code. Only populated for states in `states`. */
  countiesByState?: Record<string, string[]>;
  /** @deprecated Legacy NJ-only field. Read via normalizeMapContent; writers should emit `states`/`countiesByState`. */
  counties?: string[];
}

export interface ContactContent {
  title: string;
  showQuoteForm: boolean;
  phone: string | null;
  email: string | null;
  showPhone?: boolean;
  showEmail?: boolean;
  showAddress?: boolean;
  showWebsite?: boolean;
}

export interface FooterContent {
  showSocialLinks: boolean;
  /** @deprecated Use showPhone, showEmail, showAddress instead */
  showContactInfo?: boolean;
  showPhone?: boolean;
  showEmail?: boolean;
  showAddress?: boolean;
  showSitemap?: boolean;
  /** Quick-action row (Call / Email / Get a Quote) above the main footer area. */
  showQuickContactBar?: boolean;
  /** Free-text line under the logo (e.g. "Trusted residential appraisals across New Jersey"). */
  tagline?: string;
  socialLinks: {
    facebook: string | null;
    linkedin: string | null;
    instagram: string | null;
    x: string | null;
    google: string | null;
  };
  copyrightText: string;
  showAppraiserLogin?: boolean;
  /** Background style for footer */
  fallbackBackground?: FallbackBackground;
}

// FAQ section types
export interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export interface FAQContent {
  title: string;
  subtitle?: string;
  items: FAQItem[];
}

// Process/Timeline section types
export interface ProcessStep {
  id: string;
  number: number;
  title: string;
  description: string;
  icon?: string;
}

export interface ProcessContent {
  title: string;
  subtitle?: string;
  steps: ProcessStep[];
}

// Testimonials section types
export interface TestimonialItem {
  id: string;
  name: string;
  location?: string;
  rating: number;
  text: string;
  date?: string;
  serviceType?: string;
  /**
   * Starter-content marker (D3). Placeholder items are visible in the editor
   * preview (with an "Example" badge) but hidden on the published site, so a
   * brand-new tenant never broadcasts the same fake testimonials. The flag
   * clears automatically the moment the appraiser edits the item.
   */
  placeholder?: boolean;
}

export interface TestimonialsContent {
  title: string;
  subtitle?: string;
  items: TestimonialItem[];
  showRatings: boolean;
  /**
   * Visual layout (B3). `grid` — the original 3-column responsive grid;
   * `carousel` — horizontal Embla swiper with prev/next controls. Defaults
   * to `grid` when unset for back-compat with existing sites.
   */
  layout?: 'grid' | 'carousel';
}

// Credentials section types
export interface License {
  type: string;
  number: string;
  state: string;
}

export interface CredentialsContent {
  title: string;
  subtitle?: string;
  licenses: License[];
  affiliations: string[];
  designations: string[];
}

// Stats section types
export interface StatItem {
  value: string;
  label: string;
  icon?: string;
}

export type LiveStatType = 'totalAppraisals' | 'avgTurnTime' | 'propertyTypes' | 'countyHeatmap';

export interface StatsContent {
  title?: string;
  stats: StatItem[];
  liveStats?: {
    enabled: boolean;
    metrics: LiveStatType[];
  };
  showCountyHeatmap?: boolean;
}

// Property Lookup section types
export interface PropertyLookupContent {
  title: string;
  subtitle?: string;
  showPoweredBy: boolean;
}

// Blog section types
export interface BlogContent {
  title: string;
  subtitle?: string;
  showFeaturedOnly?: boolean;
}

// Google Review section types
export interface GoogleReviewContent {
  title: string;
  subtitle?: string;
  reviewUrl: string;
  buttonText: string;
}

// Video Intro section types
export interface VideoIntroContent {
  title: string;
  subtitle?: string;
  videoUrl: string;
  thumbnailUrl?: string | null;
}

// Fee Schedule section types
export interface FeeScheduleItem {
  id: string;
  service: string;
  priceRange: string;
  turnaround?: string;
  notes?: string;
}

export interface FeeScheduleContent {
  title: string;
  subtitle?: string;
  items: FeeScheduleItem[];
  disclaimer?: string;
}

// Announcement banner types
export interface AnnouncementContent {
  text: string;
  linkText?: string;
  linkUrl?: string;
  backgroundColor?: string;
  textColor?: string;
  enabled: boolean;
}

// Scheduling section types
export interface SchedulingContent {
  title: string;
  subtitle?: string;
  description?: string;
  availableDays?: string[];
  availableHours?: { start: string; end: string };
}

// Comparison table section types
export interface ComparisonItem {
  id: string;
  feature: string;
  columnA: string;
  columnB: string;
}

export interface ComparisonContent {
  title: string;
  subtitle?: string;
  columnAHeader: string;
  columnBHeader: string;
  items: ComparisonItem[];
  highlightColumn?: 'A' | 'B';
}

// Office location map section types
export interface OfficeMapContent {
  title: string;
  subtitle?: string;
  address: string;
  embedUrl?: string;
}

/**
 * Sticky quick-contact bar (B6) — a fixed-bottom action strip with
 * tap-to-call / email / quote-request buttons. Designed to stay in view
 * while the visitor scrolls; opt-in section type.
 */
export interface StickyContactContent {
  /** Phone number to populate the call button. Empty = no call button. */
  phone?: string;
  /** Email address for the mail button. Empty = no email button. */
  email?: string;
  /** Show a "Request Quote" button that opens the QuoteRequestModal. */
  showQuoteButton?: boolean;
  /** Override the quote button's label (defaults to "Quote"). */
  quoteButtonLabel?: string;
  /** `mobile-only` hides the bar at ≥md breakpoint; `always` shows everywhere. */
  visibility?: 'mobile-only' | 'always';
}

/**
 * Social-proof bar (B4) — a thin themed strip of credentialing /
 * experience signals (icon + short label). Reuses `TrustBadge` so the
 * icon set stays curated and consistent with the hero badges from B1.
 */
export interface SocialProofContent {
  /** Optional small heading above the strip — leave blank for a bare badge row. */
  title?: string;
  items: TrustBadge[];
}

/**
 * Values / mission grid (B5) — 3-4 icon + headline + blurb cards under a
 * shared section title. Icons reuse the curated `TrustBadgeIcon` set.
 */
export interface ValuesItem {
  id: string;
  icon: TrustBadgeIcon;
  title: string;
  description: string;
}

export interface ValuesContent {
  title: string;
  subtitle?: string;
  items: ValuesItem[];
}

/**
 * Resources / trusted-professionals section (D2).
 *
 * The appraiser's curated list of allied pros they regularly work with —
 * realtors, lenders, attorneys, inspectors. Renders as an inline section,
 * a dedicated `/resources` page, or both. Content is fully appraiser-authored
 * (no shared platform list — every appraiser has different partners).
 */
export type ResourceProfession = 'realtor' | 'lender' | 'attorney' | 'inspector' | 'other';

export interface ResourceContact {
  id: string;
  /** Person's name, e.g. "Jane Smith". */
  name: string;
  /** Profession bucket — controls grouping and the default icon. */
  profession: ResourceProfession;
  /** Free-form role title, e.g. "Buyer's Agent", "VP of Lending". */
  role?: string;
  company?: string;
  phone?: string;
  email?: string;
  /** Full URL (with scheme) to the professional's site or profile. */
  website?: string;
  /** One- or two-sentence blurb shown on the card. */
  bio?: string;
  /** Optional headshot URL — supabase storage or external. */
  photoUrl?: string | null;
}

export interface ResourcesContent {
  title: string;
  subtitle?: string;
  /** Optional intro paragraph above the grid. */
  introText?: string;
  /**
   * Disclaimer text shown below the grid. Defaults to a RESPA-aware note about
   * referrals not constituting endorsement or compensation; editable per site.
   */
  disclaimer?: string;
  items: ResourceContact[];
}

/**
 * Glossary section content (D1).
 *
 * Surfaces the platform's shared 121-term glossary as an opt-in teaser on the
 * home page plus dedicated `/glossary` (and `/glossary/{slug}`) pages. Content
 * itself is platform-authored — see `src/constants/glossaryTerms.ts` — so this
 * shape only carries per-site presentation knobs.
 */
export interface GlossaryContent {
  title: string;
  subtitle?: string;
  /**
   * Optional category to feature in the inline teaser. Matches a
   * `GlossaryCategory` string. When unset, the teaser samples across all
   * categories.
   */
  featuredCategory?: string;
  /** Number of terms to show in the inline teaser. Defaults to 6. */
  maxTerms?: number;
  /** Override for the "Browse all terms" CTA label. */
  browseAllText?: string;
}

// Expanded section types
export type SectionType =
  | 'about'
  | 'services'
  | 'map'
  | 'contact'
  | 'faq'
  | 'process'
  | 'testimonials'
  | 'credentials'
  | 'stats'
  | 'propertyLookup'
  | 'blog'
  | 'googleReview'
  | 'videoIntro'
  | 'feeSchedule'
  | 'announcement'
  | 'scheduling'
  | 'comparison'
  | 'officeMap'
  | 'glossary'
  | 'resources'
  | 'socialProof'
  | 'values'
  | 'stickyContact';

// Union type for all section content types
export type SectionContent =
  | AboutContent
  | ServicesContent
  | MapContent
  | ContactContent
  | FAQContent
  | ProcessContent
  | TestimonialsContent
  | CredentialsContent
  | StatsContent
  | PropertyLookupContent
  | BlogContent
  | GoogleReviewContent
  | VideoIntroContent
  | FeeScheduleContent
  | AnnouncementContent
  | SchedulingContent
  | ComparisonContent
  | OfficeMapContent
  | GlossaryContent
  | ResourcesContent
  | SocialProofContent
  | ValuesContent
  | StickyContactContent;

export type DisplayMode = 'inline' | 'page' | 'both';

// Decorative background options per section
export type DecorativePattern = 'none' | 'dots' | 'grid' | 'diagonal' | 'topography' | 'circuit';
export type DecorativeGradient = 'none' | 'radial-glow' | 'top-fade' | 'bottom-fade' | 'corner-accent';
export type DecorativeShape = 'none' | 'blob-top-right' | 'blob-bottom-left' | 'circle-scatter' | 'ring-accent';

/** Accent motif IDs and placement options (B7). */
export type DecorativeMotif =
  | 'none'
  | 'house'
  | 'key'
  | 'blueprint'
  | 'magnifier'
  | 'document'
  | 'map-pin';
export type DecorativeMotifPlacement =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'center';

export interface SectionDecorativeConfig {
  pattern?: DecorativePattern;
  gradient?: DecorativeGradient;
  shape?: DecorativeShape;
  parallaxImage?: string | null;
  patternOpacity?: number;
  /** Curated accent SVG motif (B7). */
  motif?: DecorativeMotif;
  motifPlacement?: DecorativeMotifPlacement;
  /** Motif fill opacity, 0–0.25. Defaults to ~0.06 — soft watermark. */
  motifOpacity?: number;
}

/**
 * Per-page SEO override (F2). Applied when the section has its own URL
 * (displayMode `'page'` or `'both'`). Empty / missing fields fall back to the
 * page's auto-generated values — so the appraiser only has to tune what
 * they actually want to change.
 */
export interface SectionPageMeta {
  /** `<title>` override. */
  title?: string;
  /** `<meta name="description">` + OG description override. */
  description?: string;
  /** Open Graph image URL override. */
  ogImage?: string | null;
}

export interface SiteSection<T = SectionContent> {
  id: string;
  type: SectionType;
  enabled: boolean;
  content: T;
  /** Where the section appears: 'inline' (home), 'page' (dedicated URL), or 'both' (summary + page) */
  displayMode?: DisplayMode;
  /** Custom URL slug for dedicated page (defaults to section type) */
  pageSlug?: string;
  /** Whether to show this section in navigation */
  showInNav?: boolean;
  /** Decorative background config */
  decorative?: SectionDecorativeConfig;
  /** Per-page SEO overrides applied when this section has its own URL (F2). */
  pageMeta?: SectionPageMeta;
}

export interface InnerHeroContent {
  backgroundImage: string | null;
  parallax?: boolean;
  overlayType?: HeroOverlayType;
  overlayColor?: string;
  overlayOpacity?: number;
  /** Blend direction for the 'gradient-panel' overlay type. Defaults to 'side'. */
  panelBlend?: HeroPanelBlend;
  /** See `HeroContent.panelCoverage` — same semantics for the inner hero. */
  panelCoverage?: number;
  /** See `HeroContent.panelUseSecondary` — same semantics for the inner hero. */
  panelUseSecondary?: boolean;
  textAlign?: HeroTextAlign;
  textColor?: string;
  /** Background style when no image is set */
  fallbackBackground?: FallbackBackground;
}

/** What kind of place a location page covers. Drives labels, the map, and SEO. */
export type LocationKind = 'county' | 'city' | 'region' | 'custom';

/** A sub-area listed on a location page (a municipality, town, or neighborhood). */
export interface LocationArea {
  name: string;
  /** Freeform descriptor, e.g. "Township", "Borough", "Neighborhood". */
  type?: string;
}

/**
 * An editable, state-agnostic location landing page (C1). Replaces the
 * hardcoded NJ-only county pages. Stored in `SiteData.locationPages`.
 */
export interface LocationPage {
  id: string;
  enabled: boolean;
  kind: LocationKind;
  /** USPS state code, e.g. 'NJ'. */
  stateCode: string;
  /** Display name, e.g. "Bergen" or "Hoboken". */
  name: string;
  /** URL slug, e.g. "bergen-county". Preserved across edits to protect SEO. */
  slug: string;
  /** County seat / hub town. Optional — typically blank for cities. */
  seat?: string;
  /** Page H1 override. When absent, a headline is generated from the name. */
  headline?: string;
  /** Intro paragraph. A page needs this filled before it is indexable. */
  description: string;
  highlights: string[];
  /** Municipalities / towns / neighborhoods covered. */
  areas: LocationArea[];
  /** Show the state county map (honored only for counties in a mapped state). */
  showMap?: boolean;
  /** Optional SEO overrides; fall back to generated values. */
  metaTitle?: string;
  metaDescription?: string;
  /** Provenance: the `stateCode:slug` of the library entry this was seeded from. */
  seededFrom?: string;
}

/**
 * An editable service landing page (C2). Mirrors C1's `LocationPage` shape
 * for service offerings — replaces the read-only `ServicePageData` library
 * lookup so appraisers can refine copy per service without code edits.
 *
 * `seededFrom` records the library service id this page was generated from,
 * so the editor can detect still-default starter text the same way C1 does.
 */
export interface ServicePage {
  id: string;
  enabled: boolean;
  /** URL slug — `/{slug}` on the tenant site. Preserved across edits for SEO. */
  slug: string;
  /** Headline / `<h1>` for the page, e.g. "Single-Family Home Appraisals". */
  title: string;
  /** One-sentence summary used in cards, link previews, and the home grid. */
  shortDescription: string;
  /** Intro paragraph — required before the page is indexable (SEO guardrail). */
  overview: string;
  /** Bulleted reasons clients need this service. */
  whyNeeded: string[];
  /** Bulleted items the appraiser examines / covers in this service. */
  whatWeExamine: string[];
  /** Audience tags shown as chips ("Homebuyers", "Estate executors", …). */
  idealFor: string[];
  /** Optional SEO overrides; fall back to generated values. */
  metaTitle?: string;
  metaDescription?: string;
  /** Provenance: the library service id (e.g. `single-family`) this was seeded from. */
  seededFrom?: string;
}

/**
 * Per-tenant analytics tag IDs (G1).
 *
 * Each field is the *appraiser's own* property — injected on the public site
 * in addition to Ashlar's platform-wide GA pixel. All optional; an empty
 * string is treated the same as missing. The injection layer is
 * `MemberSiteCustomAnalytics` in `SubdomainSiteRouter`.
 */
export interface SiteAnalytics {
  /** Google Analytics 4 Measurement ID, e.g. `G-XXXXXXXXXX`. */
  ga4MeasurementId?: string;
  /** Google Tag Manager Container ID, e.g. `GTM-XXXXXXX`. */
  gtmContainerId?: string;
  /** Microsoft Clarity Project ID, e.g. `abcd1234ef`. */
  clarityProjectId?: string;
  /**
   * Google Ads conversion ID (G2), e.g. `AW-XXXXXXXXX`. When set,
   * `MemberSiteCustomAnalytics` loads the Ads gtag and registers the property.
   */
  googleAdsConversionId?: string;
  /**
   * Conversion label paired with the Ads ID — e.g. `AbCdEfGhIj`. When BOTH
   * fields are set, lead-form submissions fire
   * `gtag('event', 'conversion', { send_to: 'AW-XXX/LABEL', ... })` in addition
   * to the GA4 `generate_lead` event (see `src/lib/leadTracking.ts`).
   */
  googleAdsLeadConversionLabel?: string;
}

/** Pricing tier — a single sqft bracket → price row. */
export interface PricingTier {
  id: string;
  /** Inclusive lower bound (sqft). */
  minSqft: number;
  /** Inclusive upper bound (sqft). `null` = no upper bound (largest tier). */
  maxSqft: number | null;
  /** Base price in whole USD. */
  price: number;
}

/** Per-service override of the global tier table. */
export interface ServicePricingOverride {
  /** Matches ServiceItem.id. */
  serviceId: string;
  tiers: PricingTier[];
}

export interface UrgencyMultipliers {
  standard?: number;
  rush?: number;
  urgent?: number;
  flexible?: number;
}

/** Optional instant-quote configuration. Stored on SiteData.pricing. */
export interface PricingConfig {
  /** When false, no instant quotes appear regardless of tier setup. */
  enabled: boolean;
  /** Default tier table — applies to any service that doesn't override. */
  globalTiers?: PricingTier[];
  /** Per-service overrides. Each only applies if the service is enabled. */
  serviceOverrides?: ServicePricingOverride[];
  /** Multipliers applied to the matched tier price. Defaults: standard 1.0, rush 1.25, urgent 1.5, flexible 1.0. */
  urgencyMultipliers?: UrgencyMultipliers;
  /** Universal disclaimer rendered alongside every quote (e.g. "Estimate only — final fee determined upon inspection."). */
  disclaimer?: string;
}

/** Built-in legal pages (Privacy / Terms / Accessibility) editable in the site builder. */
export interface LegalPageContent {
  enabled: boolean;
  /** Override for the default title (e.g. "Privacy Policy"). */
  title?: string;
  /** Rich-text body as TipTap JSON. Rendered with `prose` styling. */
  body: any | null;
  /** Free-form "last modified" stamp (e.g. "April 2, 2026"). Rendered in the hero and at the bottom of the body. Leave blank to omit. */
  lastModified?: string;
  pageMeta?: SectionPageMeta;
}

export interface LegalPages {
  privacy?: LegalPageContent;
  terms?: LegalPageContent;
  accessibility?: LegalPageContent;
}

export type LegalPageKey = keyof LegalPages;

export interface SiteData {
  theme: SiteTheme;
  hero: HeroContent;
  innerHero?: InnerHeroContent;
  sections: SiteSection[];
  footer: FooterContent;
  /** Editable location landing pages (C1). When absent, derived from the map section. */
  locationPages?: LocationPage[];
  /** Editable service landing pages (C2). When absent, derived from selected services. */
  servicePages?: ServicePage[];
  /** Built-in privacy/terms/accessibility pages. Each only exists when the appraiser enables it. */
  legalPages?: LegalPages;
  /** Optional instant-quote pricing — when set, the quote-request modal shows a live estimate. */
  pricing?: PricingConfig;
  /** Per-tenant analytics tag IDs (G1). */
  analytics?: SiteAnalytics;
}

export interface AppraiserSite {
  id: string;
  user_id: string;
  subdomain: string;
  custom_domain: string | null;
  site_data: SiteData;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface SiteInquiry {
  id: string;
  site_id: string;
  name: string;
  email: string;
  phone: string | null;
  property_address: string | null;
  appraisal_type: string | null;
  message: string | null;
  is_read: boolean;
  status: 'new' | 'contacted' | 'quote_sent' | 'converted' | 'archived';
  converted_appraisal_id: string | null;
  created_at: string;
}

// NJ Counties constant
export const NJ_COUNTIES = [
  'Atlantic', 'Bergen', 'Burlington', 'Camden', 'Cape May',
  'Cumberland', 'Essex', 'Gloucester', 'Hudson', 'Hunterdon',
  'Mercer', 'Middlesex', 'Monmouth', 'Morris', 'Ocean',
  'Passaic', 'Salem', 'Somerset', 'Sussex', 'Union', 'Warren'
] as const;

export type NJCounty = typeof NJ_COUNTIES[number];

// Default site data generator
export const createDefaultSiteData = (agentData?: {
  name?: string | null;
  tagline?: string | null;
  about?: string | null;
  bio?: string | null;
  headshot_url?: string | null;
  years_experience?: number | null;
  certifications?: string[] | null;
  specialties?: string[] | null;
  phone?: string | null;
  email?: string | null;
  facebook?: string | null;
  linkedin?: string | null;
  instagram?: string | null;
  x?: string | null;
  brokerage_logo_url?: string | null;
}): SiteData => {
  const name = agentData?.name || 'Professional Appraiser';
  
  return {
    theme: {
      primaryColor: '#1a365d',
      secondaryColor: '#4A5568',
      fontFamily: 'Inter',
      logoUrl: agentData?.brokerage_logo_url || null,
    },
    hero: {
      title: name,
      subtitle: agentData?.tagline || 'Certified Real Estate Appraisals',
      backgroundImage: null,
      ctaText: 'Request a Quote',
      ctaLink: '#contact',
    },
    sections: [
      {
        id: 'about',
        type: 'about',
        enabled: true,
        content: {
          title: 'About Me',
          bio: agentData?.about || agentData?.bio || '',
          headshotUrl: agentData?.headshot_url || null,
          yearsExperience: agentData?.years_experience || null,
          certifications: agentData?.certifications || [],
        } as AboutContent,
      },
      {
        id: 'services',
        type: 'services',
        enabled: true,
        content: {
          title: 'Services',
          items: agentData?.specialties || ['Residential Appraisals', 'FHA/VA Appraisals', 'Estate & Divorce Appraisals'],
        } as ServicesContent,
      },
      {
        id: 'process',
        type: 'process',
        enabled: true,
        content: {
          title: 'How It Works',
          steps: DEFAULT_PROCESS_STEPS,
        } as ProcessContent,
      },
      {
        id: 'service-area',
        type: 'map',
        enabled: true,
        content: {
          title: 'Service Area',
          states: [],
          countiesByState: {},
        } as MapContent,
      },
      {
        id: 'testimonials',
        type: 'testimonials',
        enabled: true,
        content: {
          title: 'What Clients Say',
          // D3 starter content — items are `placeholder: true` so they only
          // appear in the editor preview; the published site renders nothing
          // until the appraiser replaces them with real testimonials.
          items: DEFAULT_TESTIMONIAL_PLACEHOLDERS,
          showRatings: true,
        } as TestimonialsContent,
      },
      {
        id: 'faq',
        type: 'faq',
        enabled: true,
        content: {
          title: 'Frequently Asked Questions',
          items: DEFAULT_FAQ_ITEMS,
        } as FAQContent,
      },
      {
        id: 'contact',
        type: 'contact',
        enabled: true,
        content: {
          title: 'Get in Touch',
          showQuoteForm: true,
          phone: agentData?.phone || null,
          email: agentData?.email || null,
        } as ContactContent,
      },
    ],
    footer: {
      showSocialLinks: true,
      socialLinks: {
        facebook: agentData?.facebook || null,
        linkedin: agentData?.linkedin || null,
        instagram: agentData?.instagram || null,
        x: agentData?.x || null,
        google: null,
      },
      copyrightText: `© ${new Date().getFullYear()} ${name}. All rights reserved.`,
    },
  };
};

/**
 * Validates that a JSONB value has the expected SiteData shape.
 * Returns the data with defaults applied for any missing top-level keys,
 * preventing runtime crashes from malformed database records.
 */
export const validateSiteData = (raw: unknown): SiteData | null => {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, any>;

  // Must have theme, hero, sections, footer at minimum
  if (!data.theme || typeof data.theme !== 'object') return null;
  if (!data.hero || typeof data.hero !== 'object') return null;
  if (!Array.isArray(data.sections)) return null;

  return {
    theme: {
      primaryColor: data.theme.primaryColor || '#1a365d',
      secondaryColor: data.theme.secondaryColor || '#4A5568',
      fontFamily: data.theme.fontFamily || 'Inter',
      ...data.theme,
    },
    hero: {
      title: data.hero.title || '',
      subtitle: data.hero.subtitle || '',
      backgroundImage: data.hero.backgroundImage || null,
      ctaText: data.hero.ctaText || 'Request a Quote',
      ...data.hero,
    },
    sections: data.sections,
    footer: data.footer && typeof data.footer === 'object'
      ? data.footer as FooterContent
      : {
          showSocialLinks: false,
          socialLinks: { facebook: null, linkedin: null, instagram: null, x: null, google: null },
          copyrightText: `© ${new Date().getFullYear()} All rights reserved.`,
        },
    ...(data.innerHero ? { innerHero: data.innerHero } : {}),
    ...(Array.isArray(data.locationPages) ? { locationPages: data.locationPages } : {}),
    ...(Array.isArray(data.servicePages) ? { servicePages: data.servicePages } : {}),
    ...(data.legalPages && typeof data.legalPages === 'object' ? { legalPages: data.legalPages } : {}),
    ...(data.pricing && typeof data.pricing === 'object' ? { pricing: data.pricing } : {}),
    ...(data.analytics && typeof data.analytics === 'object' ? { analytics: data.analytics } : {}),
  } as SiteData;
};

// Generate subdomain from name
export const generateSubdomain = (name: string | null | undefined): string => {
  if (!name) return `appraiser-${Date.now()}`;
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50) || `appraiser-${Date.now()}`;
};
